/*
 * Store stock check
 * -----------------
 * Drives the "check in-store stock" button and modal rendered by
 * snippets/store-stock-check.liquid.
 *
 * Step 1 lists the product's variants grouped by colour.
 * Step 2 asks the backend which stores hold the chosen variant and lists them.
 *
 * ES5 only (no build step). All API-supplied text is inserted with
 * textContent, never innerHTML.
 */
(function () {
  'use strict';

  var REQUEST_TIMEOUT_MS = 8000;
  var VISIBLE_CLASS = 'cc-popup--visible';
  var BODY_NO_SCROLL_CLASS = 'cc-popup-no-scroll';
  var FOCUSABLE_SELECTOR =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function $$(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function parseJSONAttribute(element, attribute, fallback) {
    var raw = element.getAttribute(attribute);
    if (!raw) {
      return fallback;
    }
    try {
      return JSON.parse(raw);
    } catch (error) {
      return fallback;
    }
  }

  /* Only allow links the API is expected to return. */
  function safeHttpUrl(value) {
    if (typeof value !== 'string') {
      return null;
    }
    var trimmed = value.replace(/^\s+|\s+$/g, '');
    return /^https?:\/\//i.test(trimmed) ? trimmed : null;
  }

  function createElement(tagName, className, text) {
    var element = document.createElement(tagName);
    if (className) {
      element.className = className;
    }
    if (text !== undefined && text !== null && text !== '') {
      element.textContent = String(text);
    }
    return element;
  }

  function empty(element) {
    while (element.firstChild) {
      element.removeChild(element.firstChild);
    }
  }

  /*
   * Minimal popup controller. The theme's own ccPopup class is scoped inside
   * the theme.js IIFE and is not reachable from here, so this reimplements the
   * open/close behaviour against the same CSS classes.
   */
  function Popup(container) {
    this.container = container;
    this.previouslyActiveElement = null;
    this.onKeydown = this.handleKeydown.bind(this);
  }

  /*
   * aria-modal tells assistive tech the rest of the page is inert, but it does
   * not stop Tab from walking out of the dialog, so the cycle is enforced here.
   */
  Popup.prototype.getFocusableElements = function () {
    return $$(this.container, FOCUSABLE_SELECTOR).filter(function (element) {
      if (element.disabled || element.getAttribute('aria-hidden') === 'true') {
        return false;
      }
      /* offsetParent is null for display:none subtrees; also covers hidden steps. */
      return element.offsetWidth > 0 || element.offsetHeight > 0 || element.offsetParent !== null;
    });
  };

  Popup.prototype.handleKeydown = function (event) {
    if (event.keyCode === 27) {
      this.close();
      return;
    }

    if (event.keyCode !== 9) {
      return;
    }

    var focusable = this.getFocusableElements();
    if (!focusable.length) {
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || focusable.indexOf(active) === -1) {
        event.preventDefault();
        last.focus();
      }
      return;
    }

    if (active === last || focusable.indexOf(active) === -1) {
      event.preventDefault();
      first.focus();
    }
  };

  Popup.prototype.isOpen = function () {
    return this.container.classList.contains(VISIBLE_CLASS);
  };

  Popup.prototype.open = function () {
    this.previouslyActiveElement = document.activeElement;
    document.body.classList.add(BODY_NO_SCROLL_CLASS);
    this.container.classList.add(VISIBLE_CLASS);
    this.container.setAttribute('aria-hidden', 'false');
    window.addEventListener('keydown', this.onKeydown);

    var closeButton = this.container.querySelector('.cc-popup-close');
    if (closeButton) {
      setTimeout(function () {
        closeButton.focus();
      }, 300);
    }
  };

  Popup.prototype.close = function () {
    this.container.classList.remove(VISIBLE_CLASS);
    this.container.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(BODY_NO_SCROLL_CLASS);
    window.removeEventListener('keydown', this.onKeydown);

    if (this.previouslyActiveElement && this.previouslyActiveElement.focus) {
      this.previouslyActiveElement.focus();
    }
    this.previouslyActiveElement = null;
  };

  function StoreStockCheck(root) {
    this.root = root;
    this.endpoint = (root.getAttribute('data-endpoint') || '').replace(/\/+$/, '');
    this.variants = parseJSONAttribute(root, 'data-variants', []);
    this.strings = parseJSONAttribute(root, 'data-strings', {});
    this.currentVariantId = root.getAttribute('data-current-variant-id') || '';
    /*
     * Bumped whenever the customer moves on (back to the list, or a new
     * lookup). A response whose generation no longer matches belongs to an
     * abandoned request and must not overwrite what is on screen.
     */
    this.requestGeneration = 0;

    this.modal = root.querySelector('.store-stock-check-modal');
    this.trigger = root.querySelector('[data-store-stock-open]');
    this.variantsStep = root.querySelector('[data-store-stock-step="variants"]');
    this.resultStep = root.querySelector('[data-store-stock-step="result"]');
    this.variantsContainer = root.querySelector('[data-store-stock-variants]');
    this.resultContainer = root.querySelector('[data-store-stock-result]');
    this.selectedLabel = root.querySelector('[data-store-stock-selected]');

    if (!this.endpoint || !this.modal || !this.trigger || !this.variantsContainer) {
      return;
    }

    /*
     * The modal is position:fixed but sits inside animated (transformed)
     * ancestors on the product page, which would break that positioning.
     * theme.js moves its own popups to the body for the same reason.
     */
    var productId = root.getAttribute('data-product-id') || '';
    this.modal.setAttribute('data-store-stock-modal', productId);
    /* Drop a modal left behind by a previous render of the same section. */
    $$(document.body, '[data-store-stock-modal="' + productId + '"]').forEach(function (stale) {
      if (stale !== root.querySelector('.store-stock-check-modal') && stale.parentNode === document.body) {
        stale.parentNode.removeChild(stale);
      }
    });
    document.body.appendChild(this.modal);

    this.popup = new Popup(this.modal);
    this.bindEvents();
    this.renderVariants();
  }

  StoreStockCheck.prototype.bindEvents = function () {
    var self = this;

    this.trigger.addEventListener('click', function (event) {
      event.preventDefault();
      self.showVariantsStep();
      self.popup.open();
    });

    $$(this.modal, '.cc-popup-close, .cc-popup-background').forEach(function (element) {
      element.addEventListener('click', function (event) {
        event.preventDefault();
        self.popup.close();
      });
    });

    var backButton = this.modal.querySelector('[data-store-stock-back]');
    if (backButton) {
      backButton.addEventListener('click', function (event) {
        event.preventDefault();
        self.showVariantsStep();
      });
    }

    /*
     * The theme broadcasts variant changes as a jQuery custom event, which
     * native listeners cannot see, so this needs the theme's jQuery instance.
     */
    var jq = (window.theme && window.theme.jQuery) || window.jQuery;
    if (jq) {
      jq(window).on('cc-variant-updated.store-stock-check', function (event, args) {
        if (args && args.variant && args.variant.id) {
          self.currentVariantId = String(args.variant.id);
          self.highlightCurrentVariant();
        }
      });
    }
  };

  StoreStockCheck.prototype.showVariantsStep = function () {
    /* Abandon any in-flight lookup: its result is no longer being shown. */
    this.requestGeneration += 1;
    this.variantsStep.hidden = false;
    this.resultStep.hidden = true;
  };

  StoreStockCheck.prototype.showResultStep = function () {
    this.variantsStep.hidden = true;
    this.resultStep.hidden = false;
  };

  /* Group the variants by colour, preserving the product's own order. */
  StoreStockCheck.prototype.groupVariantsByColour = function () {
    var groups = [];
    var index = {};

    this.variants.forEach(function (variant) {
      var key = variant.color || '';
      if (!Object.prototype.hasOwnProperty.call(index, key)) {
        index[key] = groups.length;
        groups.push({ color: key, image: variant.image || '', variants: [] });
      }
      var group = groups[index[key]];
      if (!group.image && variant.image) {
        group.image = variant.image;
      }
      group.variants.push(variant);
    });

    return groups;
  };

  StoreStockCheck.prototype.renderVariants = function () {
    var self = this;
    empty(this.variantsContainer);

    this.groupVariantsByColour().forEach(function (group) {
      var groupElement = createElement('div', 'store-stock-check__group');

      var header = createElement('div', 'store-stock-check__group-header');
      if (group.image) {
        var image = document.createElement('img');
        image.className = 'store-stock-check__thumb';
        image.src = group.image;
        image.alt = '';
        image.loading = 'lazy';
        image.width = 48;
        image.height = 48;
        header.appendChild(image);
      }
      header.appendChild(createElement('span', 'store-stock-check__color', group.color));
      groupElement.appendChild(header);

      group.variants.forEach(function (variant) {
        var row = createElement('div', 'store-stock-check__row');
        row.setAttribute('data-variant-id', variant.id);
        row.appendChild(createElement('span', 'store-stock-check__size', variant.size || variant.title));

        var button = createElement(
          'button',
          'button alt store-stock-check__row-button',
          self.strings.check || ''
        );
        button.type = 'button';
        button.addEventListener('click', function (event) {
          event.preventDefault();
          self.checkVariant(variant);
        });
        row.appendChild(button);
        groupElement.appendChild(row);
      });

      self.variantsContainer.appendChild(groupElement);
    });

    this.highlightCurrentVariant();
  };

  StoreStockCheck.prototype.highlightCurrentVariant = function () {
    var current = String(this.currentVariantId || '');
    $$(this.variantsContainer, '.store-stock-check__row').forEach(function (row) {
      var isCurrent = current !== '' && row.getAttribute('data-variant-id') === current;
      if (isCurrent) {
        row.classList.add('store-stock-check__row--current');
      } else {
        row.classList.remove('store-stock-check__row--current');
      }
    });
  };

  StoreStockCheck.prototype.checkVariant = function (variant) {
    var self = this;

    this.requestGeneration += 1;
    var generation = this.requestGeneration;

    this.showResultStep();
    this.renderSelectedLabel(variant);
    empty(this.resultContainer);
    this.resultContainer.appendChild(
      createElement('p', 'store-stock-check__state', this.strings.loading || '')
    );

    this.fetchStock(variant.id, function (error, data) {
      /* A slower earlier request must not overwrite a newer result. */
      if (generation !== self.requestGeneration) {
        return;
      }
      if (error || !data) {
        self.renderError();
        return;
      }
      self.renderLocations(data.locations || []);
    });
  };

  StoreStockCheck.prototype.renderSelectedLabel = function (variant) {
    if (!this.selectedLabel) {
      return;
    }
    var parts = [];
    if (variant.color) {
      parts.push((this.strings.colorLabel || 'COLOR') + ': ' + variant.color);
    }
    if (variant.size) {
      parts.push((this.strings.sizeLabel || 'SIZE') + ': ' + variant.size);
    }
    if (!parts.length && variant.title) {
      parts.push(variant.title);
    }
    this.selectedLabel.textContent = parts.join(' / ');
  };

  StoreStockCheck.prototype.fetchStock = function (variantId, callback) {
    if (!window.fetch) {
      callback(new Error('fetch unavailable'));
      return;
    }

    var options = {};
    var controller = null;
    var timer = null;

    if (window.AbortController) {
      controller = new window.AbortController();
      options.signal = controller.signal;
    }

    var settled = false;
    function finish(error, data) {
      if (settled) {
        return;
      }
      settled = true;
      if (timer) {
        clearTimeout(timer);
      }
      callback(error, data);
    }

    timer = setTimeout(function () {
      if (controller) {
        controller.abort();
      }
      finish(new Error('timeout'));
    }, REQUEST_TIMEOUT_MS);

    var url = this.endpoint + '/store-inventory/' + encodeURIComponent(variantId);

    window
      .fetch(url, options)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }
        return response.json();
      })
      .then(function (data) {
        finish(null, data);
      })
      .catch(function (error) {
        finish(error);
      });
  };

  StoreStockCheck.prototype.renderError = function () {
    empty(this.resultContainer);
    this.resultContainer.appendChild(
      createElement(
        'p',
        'store-stock-check__state store-stock-check__state--error',
        this.strings.error || ''
      )
    );
  };

  StoreStockCheck.prototype.renderLocations = function (locations) {
    empty(this.resultContainer);

    if (!locations.length) {
      this.resultContainer.appendChild(
        createElement('p', 'store-stock-check__state', this.strings.outOfStock || '')
      );
      return;
    }

    var self = this;

    locations.forEach(function (location) {
      var item = createElement('div', 'store-stock-check__location');

      var head = createElement('div', 'store-stock-check__location-head');
      head.appendChild(
        createElement('strong', 'store-stock-check__location-name', location.displayName || '')
      );

      var inStock = location.status === 'in_stock';
      head.appendChild(
        createElement(
          'span',
          'store-stock-check__status ' +
            (inStock
              ? 'store-stock-check__status--in-stock'
              : 'store-stock-check__status--out-of-stock'),
          inStock ? self.strings.inStock || '' : self.strings.outOfStock || ''
        )
      );
      item.appendChild(head);

      if (location.postalCode) {
        item.appendChild(
          createElement('p', 'store-stock-check__postal', '〒' + location.postalCode)
        );
      }

      if (location.address) {
        item.appendChild(createElement('p', 'store-stock-check__address', location.address));
      }

      if (location.phone) {
        var phoneWrapper = createElement('p', 'store-stock-check__phone');
        var phoneLink = createElement('a', null, location.phone);
        phoneLink.href = 'tel:' + String(location.phone).replace(/[^0-9+]/g, '');
        phoneWrapper.appendChild(phoneLink);
        item.appendChild(phoneWrapper);
      }

      var mapUrl = safeHttpUrl(location.mapUrl);
      if (mapUrl) {
        var mapWrapper = createElement('p', 'store-stock-check__map');
        var mapLink = createElement('a', null, 'Google Map');
        mapLink.href = mapUrl;
        mapLink.target = '_blank';
        mapLink.rel = 'noopener noreferrer';
        mapWrapper.appendChild(mapLink);
        item.appendChild(mapWrapper);
      }

      self.resultContainer.appendChild(item);
    });
  };

  function init() {
    $$(document, '[data-store-stock-check]').forEach(function (root) {
      if (root.getAttribute('data-store-stock-initialised') === 'true') {
        return;
      }
      root.setAttribute('data-store-stock-initialised', 'true');
      new StoreStockCheck(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-initialise when a section is re-rendered in the theme editor. */
  document.addEventListener('shopify:section:load', init);
})();
